import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { LayoutTemplate, FileDown, Mail, Pencil, Trash2, Save, Award, Star, Store, ImagePlus, X, HeartHandshake, Loader2, Megaphone, FileText, ClipboardList, Copy, GripVertical } from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TEMPLATE_ICONS = { intern_of_week: Star, certificate: Award, intern_shoutout: Megaphone, infraction_notice: ClipboardList, letterhead: FileText, partner_flyer: Store, thank_you_letter: HeartHandshake };

export default function TemplatesPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'system_admin' || user?.role === 'team_admin';
  const isDemo = user?.role === 'demo';
  const [templates, setTemplates] = useState([]);
  const [saved, setSaved] = useState([]);
  const [active, setActive] = useState(null); // template def being filled
  const [fields, setFields] = useState({});
  const [editingDocId, setEditingDocId] = useState(null);
  const [busy, setBusy] = useState('');
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [photoUploading, setPhotoUploading] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const photoKey = (i) => (i === 0 ? 'photo_url' : `photo_url_${i + 1}`);
  const capKey = (i) => (i === 0 ? 'photo_caption' : `photo_caption_${i + 1}`);

  const movePhoto = (from, to, slots) => {
    if (from === null || from === to) return;
    setFields((prev) => {
      const arr = Array.from({ length: slots }).map((_, i) => ({ url: prev[photoKey(i)], cap: prev[capKey(i)] }));
      const [m] = arr.splice(from, 1);
      arr.splice(to, 0, m);
      const next = { ...prev };
      arr.forEach((item, i) => { next[photoKey(i)] = item.url; next[capKey(i)] = item.cap; });
      return next;
    });
  };

  useEffect(() => {
    if (!active || isDemo) return;
    setPreviewLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await axios.post(`${API}/templates/${active.id}/preview`, { fields }, { responseType: 'blob' });
        setPreviewUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return URL.createObjectURL(res.data);
        });
      } catch {
        // keep last preview on transient errors
      } finally {
        setPreviewLoading(false);
      }
    }, 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, fields]);

  useEffect(() => {
    axios.get(`${API}/templates`).then(r => setTemplates(r.data.templates)).catch(() => {});
    fetchSaved();
  }, []);

  const fetchSaved = () => axios.get(`${API}/template-docs`).then(r => setSaved(r.data)).catch(() => {});

  const openTemplate = (t, doc = null) => {
    setActive(t);
    setFields(doc ? { ...doc.fields } : {});
    setEditingDocId(doc ? doc.id : null);
  };

  const validate = () => {
    const missing = active.fields.filter(f => f.required && !(fields[f.key] || '').trim());
    if (missing.length) {
      toast.error(`Please fill in: ${missing.map(f => f.label).join(', ')}`);
      return false;
    }
    return true;
  };

  const download = async () => {
    if (!validate()) return;
    setBusy('download');
    try {
      const res = await axios.post(`${API}/templates/${active.id}/render`, { fields }, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Project-LIFE-${active.id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('PDF downloaded — ready to print!');
    } catch (e) {
      toast.error(e.response?.status === 403 ? 'Demo accounts are view-only' : 'Could not generate the PDF');
    } finally { setBusy(''); }
  };

  const sendEmail = async () => {
    if (!validate()) return;
    setBusy('email');
    try {
      const res = await axios.post(`${API}/templates/${active.id}/email`, { fields, emails: emailTo });
      toast.success(`Emailed to ${res.data.sent.length} recipient${res.data.sent.length !== 1 ? 's' : ''}!`);
      setEmailOpen(false);
      setEmailTo('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send the email');
    } finally { setBusy(''); }
  };

  const saveDoc = async () => {
    if (!validate()) return;
    setBusy('save');
    const title = fields.intern_name || fields.org_name || active.name;
    try {
      if (editingDocId) {
        await axios.put(`${API}/template-docs/${editingDocId}`, { title, fields });
        toast.success('Saved changes');
      } else {
        const res = await axios.post(`${API}/template-docs`, { template_id: active.id, title, fields });
        setEditingDocId(res.data.id);
        toast.success('Saved — find it under "My saved documents"');
      }
      fetchSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save');
    } finally { setBusy(''); }
  };

  const saveAsCopy = async () => {
    if (!validate()) return;
    setBusy('copy');
    const base = fields.intern_name || fields.org_name || fields.brand_name || active.name;
    try {
      const res = await axios.post(`${API}/template-docs`, { template_id: active.id, title: `${base} (copy)`, fields });
      setEditingDocId(res.data.id);
      toast.success('Saved as a new copy — your original is untouched');
      fetchSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save a copy');
    } finally { setBusy(''); }
  };

  const deleteDoc = async (doc) => {
    if (!window.confirm(`Delete "${doc.title}"?`)) return;
    try {
      await axios.delete(`${API}/template-docs/${doc.id}`);
      setSaved(prev => prev.filter(d => d.id !== doc.id));
      toast.success('Deleted');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not delete');
    }
  };

  const uploadPhoto = async (file, key = 'photo_url') => {
    if (!file) return;
    setPhotoUploading(key);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/templates/upload-photo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setFields(prev => ({ ...prev, [key]: res.data.url }));
      toast.success('Photo added');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not upload the photo');
    } finally {
      setPhotoUploading(false);
    }
  };

  const tplById = (id) => templates.find(t => t.id === id);

  return (
    <div className="space-y-8" data-testid="templates-page">
      <div>
        <h1 className="text-3xl font-bold font-['Manrope'] text-[#1A1A1A] flex items-center gap-3">
          <LayoutTemplate className="w-8 h-8 text-[#34657f]" />
          Personalize Templates
        </h1>
        <p className="text-[#5C5C5C] mt-1">Fill in a name and details, then print as a PDF or email it to someone.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {templates.map((t) => {
          const Icon = TEMPLATE_ICONS[t.id] || LayoutTemplate;
          return (
            <Card key={t.id} className="dashboard-card hover:border-[#34657f]/40 transition-colors">
              <CardContent className="p-6 flex flex-col h-full">
                <div className="w-12 h-12 rounded-xl bg-[#34657f] flex items-center justify-center mb-4">
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <h2 className="font-bold font-['Manrope'] text-lg text-[#1A1A1A]">{t.name}</h2>
                <p className="text-sm text-[#5C5C5C] mt-1 flex-1">{t.description}</p>
                <Button className="mt-4 bg-[#b34e00] hover:bg-[#a84d09]" onClick={() => openTemplate(t)} data-testid={`personalize-${t.id}-btn`}>
                  <Pencil className="w-4 h-4 mr-2" />
                  Personalize
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {saved.length > 0 && (
        <div>
          <h2 className="text-xl font-bold font-['Manrope'] text-[#1A1A1A] mb-3">
            {isAdmin ? 'All saved documents' : 'My saved documents'}
          </h2>
          <div className="space-y-2">
            {saved.map((doc) => (
              <div key={doc.id} className="flex items-center gap-3 bg-white border border-slate-200 rounded-lg px-4 py-3 flex-wrap" data-testid={`saved-doc-${doc.id}`}>
                <Badge className="bg-[#34657f]/10 text-[#34657f]">{tplById(doc.template_id)?.name || doc.template_id}</Badge>
                <span className="font-semibold text-[#1A1A1A]">{doc.title}</span>
                {isAdmin && doc.created_by !== user?.id && (
                  <span className="text-xs text-[#5C5C5C]">by {doc.created_by_name}</span>
                )}
                <span className="text-xs text-[#5C5C5C] ml-auto">{new Date(doc.updated_at).toLocaleDateString()}</span>
                <Button size="sm" variant="outline" onClick={() => openTemplate(tplById(doc.template_id), doc)} data-testid={`edit-doc-${doc.id}`}>
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
                <Button size="sm" variant="ghost" className="text-red-600" onClick={() => deleteDoc(doc)} data-testid={`delete-doc-${doc.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={!!active} onOpenChange={(o) => { if (!o) { setActive(null); setEditingDocId(null); if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); } } }}>
        <DialogContent className="sm:max-w-4xl max-h-[88vh] overflow-y-auto" data-testid="template-form-dialog">
          {active && (
            <>
              <DialogHeader>
                <DialogTitle className="font-['Manrope']">{active.name}</DialogTitle>
                <DialogDescription>{editingDocId ? 'Editing a saved document.' : 'Fill in the details below — the preview updates as you type.'}</DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-3 py-1">
                {active.fields.map((f) => (
                  <div key={f.key} className="space-y-1.5">
                    <Label htmlFor={`tf-${f.key}`}>{f.label}{f.required ? ' *' : ''}</Label>
                    {f.type === 'textarea' ? (
                      <Textarea id={`tf-${f.key}`} rows={4} value={fields[f.key] || ''} placeholder={f.placeholder}
                        onChange={(e) => setFields(prev => ({ ...prev, [f.key]: e.target.value }))}
                        data-testid={`tf-${f.key}`} />
                    ) : f.type === 'select' ? (
                      <Select value={fields[f.key] || f.default || ''}
                        onValueChange={(v) => setFields(prev => ({ ...prev, [f.key]: v }))}>
                        <SelectTrigger id={`tf-${f.key}`} data-testid={`tf-${f.key}`}>
                          <SelectValue placeholder="Choose…" />
                        </SelectTrigger>
                        <SelectContent>
                          {(f.options || []).map((o) => (
                            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input id={`tf-${f.key}`} value={fields[f.key] || ''} placeholder={f.placeholder}
                        onChange={(e) => setFields(prev => ({ ...prev, [f.key]: e.target.value }))}
                        data-testid={`tf-${f.key}`} />
                    )}
                  </div>
                ))}
                {active.supports_photo && (() => {
                  const slots = active.photo_slots || 1;
                  const multi = slots > 1;
                  const withCaptions = !!active.photo_captions;
                  return (
                    <div className="space-y-2">
                      <Label>{multi ? `Photos (up to ${slots})` : 'Photo (optional)'}</Label>
                      <div className={multi ? 'grid grid-cols-1 sm:grid-cols-3 gap-2' : ''}>
                        {Array.from({ length: slots }).map((_, i) => {
                          const key = photoKey(i);
                          const hasPhoto = !!fields[key];
                          const canDrag = multi && hasPhoto;
                          return (
                            <div
                              key={key}
                              draggable={canDrag}
                              onDragStart={() => canDrag && setDragIndex(i)}
                              onDragOver={(e) => { if (multi) e.preventDefault(); }}
                              onDrop={(e) => { if (multi) { e.preventDefault(); movePhoto(dragIndex, i, slots); setDragIndex(null); } }}
                              className={`space-y-1 ${canDrag ? 'cursor-move' : ''} ${dragIndex === i ? 'opacity-50' : ''}`}
                              data-testid={`template-photo-slot-${i}`}
                            >
                              {hasPhoto ? (
                                <div className="flex items-center gap-2 text-sm text-[#3d7a22] h-10 px-3 rounded-md border border-slate-200 bg-white">
                                  {canDrag && <GripVertical className="w-3.5 h-3.5 text-slate-400 shrink-0" />}
                                  <ImagePlus className="w-4 h-4 shrink-0" /> {multi ? `Photo ${i + 1}` : 'Photo attached'}
                                  <button type="button" className="text-red-600 underline text-xs ml-auto" onClick={() => setFields(prev => ({ ...prev, [key]: undefined, [capKey(i)]: undefined }))} data-testid={`remove-template-photo-${i}`}>
                                    <X className="w-3.5 h-3.5 inline" /> remove
                                  </button>
                                </div>
                              ) : (
                                <label className="flex items-center gap-2 h-10 px-3 rounded-md border border-dashed border-slate-300 text-sm text-[#5C5C5C] cursor-pointer hover:border-[#34657f]">
                                  <ImagePlus className="w-4 h-4 text-[#34657f] shrink-0" />
                                  <span className="truncate">{photoUploading === key ? 'Uploading…' : (multi ? `Photo ${i + 1}` : 'Add a photo of the intern')}</span>
                                  <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(e.target.files?.[0], key)} data-testid={`template-photo-input-${i}`} />
                                </label>
                              )}
                              {withCaptions && hasPhoto && (
                                <Input
                                  className="h-8 text-xs"
                                  placeholder="Short caption"
                                  value={fields[capKey(i)] || ''}
                                  onChange={(e) => setFields(prev => ({ ...prev, [capKey(i)]: e.target.value }))}
                                  data-testid={`template-photo-caption-${i}`}
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>
                      {active.id === 'popup_sale' && <p className="text-xs text-[#5C5C5C]">Captions appear under each photo (if blank, your first featured items are used).</p>}
                      {multi && <p className="text-xs text-[#5C5C5C]">Tip: drag a photo onto another to reorder them.</p>}
                    </div>
                  );
                })()}
              </div>
              <div className="hidden md:block">
                <div className="sticky top-0">
                  <p className="text-xs font-semibold text-[#5C5C5C] uppercase tracking-wide mb-2 flex items-center gap-2">
                    Live preview
                    {previewLoading && <Loader2 className="w-3.5 h-3.5 animate-spin text-[#34657f]" />}
                  </p>
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-2 min-h-[300px] flex items-center justify-center" data-testid="template-preview-panel">
                    {previewUrl ? (
                      <img src={previewUrl} alt={`Preview of ${active.name}`} className="max-w-full max-h-[58vh] shadow-md rounded" data-testid="template-preview-img" />
                    ) : (
                      <p className="text-sm text-[#5C5C5C] px-4 text-center">
                        {isDemo ? 'Preview is unavailable for demo accounts.' : 'Start typing to see your document take shape…'}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              </div>
              <DialogFooter className="flex-wrap gap-2">
                {!isDemo && (
                  <Button variant="outline" onClick={saveDoc} disabled={!!busy} data-testid="template-save-btn">
                    <Save className="w-4 h-4 mr-1.5" />{busy === 'save' ? 'Saving…' : (editingDocId ? 'Save changes' : 'Save')}
                  </Button>
                )}
                {!isDemo && editingDocId && (
                  <Button variant="outline" onClick={saveAsCopy} disabled={!!busy} data-testid="template-save-copy-btn">
                    <Copy className="w-4 h-4 mr-1.5" />{busy === 'copy' ? 'Copying…' : 'Save as a copy'}
                  </Button>
                )}
                <Button variant="outline" onClick={() => setEmailOpen(true)} disabled={!!busy} className="border-[#34657f] text-[#34657f]" data-testid="template-email-btn">
                  <Mail className="w-4 h-4 mr-1.5" />Email
                </Button>
                <Button onClick={download} disabled={!!busy} className="bg-[#34657f]" data-testid="template-download-btn">
                  <FileDown className="w-4 h-4 mr-1.5" />{busy === 'download' ? 'Generating…' : 'Download PDF'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="sm:max-w-md" data-testid="template-email-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Email this document</DialogTitle>
            <DialogDescription>The personalized PDF is attached automatically.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-1">
            <Label htmlFor="template-email-to">Recipient email(s) *</Label>
            <Input id="template-email-to" placeholder="jane@example.com, john@example.com" value={emailTo}
              onChange={(e) => setEmailTo(e.target.value)} data-testid="template-email-input" />
            <p className="text-xs text-[#5C5C5C]">Separate multiple addresses with commas (up to 10).</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailOpen(false)}>Cancel</Button>
            <Button onClick={sendEmail} disabled={busy === 'email'} className="bg-[#34657f]" data-testid="template-email-send">
              <Mail className="w-4 h-4 mr-1.5" />{busy === 'email' ? 'Sending…' : 'Send'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
